/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useContext, useState } from 'react';
import { Redirect } from 'react-router';

import { useQuery } from '@apollo/client/react';
import styled from '@emotion/styled';
import { Box, Flex, Loader, Table, TableCell, TableRow, Text } from '@opendoor/bricks/core';
import DropdownSingleSelect, {
  IDropdownSingleSelectProps,
} from '@opendoor/bricks/core/Select/DropdownSingleSelect';
import ContentCard from '@opendoor/bricks/deprecated/UNSAFE_layout/ContentCard';
import { colors, spaceWithUnits } from '@opendoor/bricks/theme/eero';

import { ContentWrapper } from '../../components/ContentWrapper';
import { LogRow, LogViewer } from '../../components/Log';
import TestModeBanner from '../../components/TestModeBanner';
import { productionClient, testingClient } from '../../config/apolloClients';
import { TestModeContext } from '../../config/context';
import { GET_LOGS, IGetLogs, IGetLogsVariables, ILogData } from '../../graphql/queries/GetLogs';
import { ERROR_PAGE_ROUTE } from '../routes';

const Button = styled('button')<{ width: string }>`
  width: ${(props) => props.width};
  margin-top: 16px;
  font-weight: bold;
  font-size: 18px;
  background-color: ${colors.brand50};
  height: 45px;
  color: ${colors.neutrals0};
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.25s ease;
  &:hover {
    background-color: #1d477b;
  }
  &:focus {
    outline: none;
  }
`;

const ButtonSecondary = styled(Button)`
  background-color: ${colors.neutrals10} !important;
  color: ${colors.neutrals70} !important;
  &:hover {
    background-color: ${colors.neutrals30} !important;
  }
`;

const enum LogFilters {
  ALL = 'all',
  ERRORS = 'errors',
}

const Usage = () => {
  const testMode = useContext(TestModeContext);

  const [logViewerOpen, setLogViewerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ILogData | undefined>(undefined);
  const [fetchingMoreLogs, setFetchingMoreLogs] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>(LogFilters.ALL);

  const { loading, error, data, fetchMore, refetch } = useQuery<IGetLogs, IGetLogsVariables>(
    GET_LOGS,
    {
      client: testMode ? testingClient : productionClient,
      notifyOnNetworkStatusChange: true,
    },
  );

  const handleLogViewerOpen = (log: ILogData) => {
    setLogViewerOpen(true);
    setSelectedLog(log);
  };

  const handleLogViewerClose = () => {
    setLogViewerOpen(false);
  };

  const fetchMoreLogs = async () => {
    setFetchingMoreLogs(true);

    await fetchMore({
      variables: {
        cursor: data!.getLogs.cursor,
        errorsOnly: activeFilter === LogFilters.ERRORS,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previousEntry = previousResult.getLogs;
        const newLogs = fetchMoreResult!.getLogs.logs;
        const newCursor = fetchMoreResult!.getLogs.cursor;

        return {
          getLogs: {
            cursor: newCursor,
            logs: [...previousEntry.logs, ...newLogs],
            __typename: previousEntry.__typename,
          },
        };
      },
    });

    setFetchingMoreLogs(false);
  };

  const handleRefreshClick = () => {
    refresh();
  };

  const refresh = (newFilter?: string) => {
    const filter = newFilter || activeFilter;

    switch (filter) {
      case LogFilters.ERRORS:
        refetch({ errorsOnly: true });
        break;
      case LogFilters.ALL:
      default:
        refetch({ errorsOnly: false });
    }
  };

  const handleFilterChange: IDropdownSingleSelectProps['onChange'] = (value) => {
    if (!value) {
      return;
    }

    const val = value.toString();
    setActiveFilter(val);
    refresh(val);
  };

  if (error) {
    return <Redirect to={ERROR_PAGE_ROUTE} />;
  }

  const noMoreLogs = data && data.getLogs && !data.getLogs.cursor;

  return (
    <ContentWrapper>
      <ContentCard
        title="Usage Logs"
        borderRadius="roundedSquare"
        paddingTop={testMode ? spaceWithUnits[10] : undefined}
      >
        {testMode ? <TestModeBanner /> : null}
        <Box mb={7} mt={3}>
          You can use the information provided here to debug and monitor your application’s
          interactions with the API.
        </Box>
        {selectedLog && (
          <LogViewer
            isOpen={logViewerOpen}
            logData={selectedLog}
            handleClose={handleLogViewerClose}
          />
        )}
        {loading && !fetchingMoreLogs ? (
          <Loader />
        ) : (
          <>
            <Flex width="100%" mb={7} justifyContent="space-between" alignItems="flex-end">
              <Flex width="180px" flexDirection="column">
                <Text as="label" id="log-filter" fontWeight="bold">
                  Filter
                </Text>
                <DropdownSingleSelect
                  aria-labelledby="log-filter"
                  selectedValue={[activeFilter]}
                  onChange={handleFilterChange}
                  placeholder="Select a filter..."
                  options={[
                    { value: LogFilters.ERRORS, label: 'Errors only' },
                    { value: LogFilters.ALL, label: 'All logs' },
                  ]}
                />
              </Flex>
              <Button width="120px" onClick={handleRefreshClick}>
                Refresh
              </Button>
            </Flex>
            <Table width="100%">
              <TableRow fontWeight="bold" paddingTop={2} paddingBottom={2}>
                <TableCell textAlign="left" fontWeight="bold">
                  STATUS
                </TableCell>
                <TableCell textAlign="left" fontWeight="bold">
                  DESCRIPTION
                </TableCell>
                <TableCell textAlign="left" fontWeight="bold">
                  DATE
                </TableCell>
              </TableRow>
              {data!.getLogs.logs.map((log, i) => (
                <LogRow data={log} key={i} onClick={handleLogViewerOpen} />
              ))}
            </Table>
            {noMoreLogs ? (
              <Text width="100%" textAlign="center" mt={3} color="neutrals70">
                No more logs to display.
              </Text>
            ) : (
              <ButtonSecondary onClick={fetchMoreLogs} width="100%">
                {fetchingMoreLogs && loading ? <Loader padding="none" /> : 'Load more'}
              </ButtonSecondary>
            )}
          </>
        )}
      </ContentCard>
    </ContentWrapper>
  );
};

export default Usage;
